import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import CustomInput from '../../../components/common/CustomInput';
import CustomSelect from '../../../components/common/CustomSelect';
import CustomFile from '../../../components/common/CustomFile';
import * as Yup from 'yup';
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance';
import axiosCommonInstance from '../../../utils/axios/axiosCommonInstance';

const validationSchema = Yup.object({
  company: Yup.string().required("Company Name is Required"),
  name: Yup.string().required("Name is Required"),
  file: Yup.mixed()
  .required("File is Required")
  .test("fileType", "Only ZIP files are allowed", (value) => {
    if (value && typeof value === "object" && "name" in value) {
      return value.name.endsWith(".zip");
    }
    return false;
  }),
});


const Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: {
      company: '',
      name: '',
      file: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  });

  const fetchCompanyData = async () => {
    try {
      const response = await axiosAuthInstance.get('company/dropdown');
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }));
        setCompanyOptions(transformedData);
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await axiosCommonInstance.get(`metaverse/detail/${id}`);
      if (response && response.data.result) {
        const metaverseData = response.data.result;

        formik.setValues({
          company: metaverseData.company._id,
          name: metaverseData.name,
          file: metaverseData.file || null,
        });
      }
    } catch (error) {
      console.error('Error fetching data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    fetchData();
  }, [id]);

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitted(true);
    try {
      const formData = new FormData();
      formData.append('company', values.company);
      formData.append('name', values.name);

      if (values.file instanceof File) {
        formData.append('file', values.file);
      } else if (typeof values.file === 'string') {
        formData.append('file', values.file);
      }

      const response = await axiosAuthInstance.put(
        `metaverse/update/${id}`,
        formData
      );
      if (response && response.status === 200) {
        resetForm();
        navigate('/metaverse');
      }
    } catch (error) {
      console.error('Error updating Metaverse', error);
    } finally {
      setIsSubmitted(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <FormikProvider value={formik}>
      <form
        className="flex flex-col p-4 gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <Field
          name="company"
          label="Select Company"
          required
          options={companyOptions}
          component={CustomSelect}
        />

        <Field
          name="name"
          label="Name"
          placeholder="Enter Name"
          required
          component={CustomInput}
        />

        <Field
          name="file"
          label="File"
          required
          accept="/.zip"
          component={CustomFile}
        />

        <button
          type="submit"
          disabled={isSubmitted}
          className="bg-primary text-xl font-medium text-white rounded-lg px-6 py-2"
        >
          {isSubmitted ? (
            <div className="w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent"></div>
          ) : (
            'Update Metaverse'
          )}
        </button>
      </form>
    </FormikProvider>
  );
};

export default Edit;